import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import Steps from '../../Common/Promotion/Steps';
import BrandCarousel from '../../Common/Promotion/BrandCarousel';
import CodeEntry from '../../Common/Promotion/CodeEntry';
import Questionaire from '../../Common/Promotion/Questionaire';
import Confirmation from '../../Common/Promotion/Confirmation';
import LegalAgreementCheckboxSet from '../../Common/Promotion/LegalAgreementCheckboxSet';
import Loader from '../../UI/Loader';
import {useTranslation} from 'react-i18next';
import ModalError from '../../UI/Modal/ModalError';
import PromotionPeriodNotification from '../../Common/Promotion/PromotionPeriodNotification';
import PepsiEaConnection from "../../Common/PepsiEa/PepsiEaConnection";
import QuestionairePostPromotion from "../../Common/Promotion/QuestionairePostPromotion";
import ConfirmationPostPromotion from "../../Common/Promotion/ConfirmationPostPromotion";

const PromotionCodeEntryEa = ({promotionSettings}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [formSubmitInProgress, setFormSubmitInProgress] = useState(false);
	const [formSubmitError, setFormSubmitError] = useState(false);
	const stepsRef = useRef(null);
	const totalSteps = useRef(null);
	const {t, i18n} = useTranslation();
	const [errorMessageCode, setErrorMessageCode] = useState();
	const [errorHeadline, setErrorHeadline] = useState();
	const [errorMessage, setErrorMessage] = useState([]);
	const [userResponse, setUserResponse] = useState();
	let fetchURL = "";
	const windowStatus = window.connectionStatus.toString();


	useEffect(() => {

		// Initialize the currentStep based on window.connectionStatus
		if (windowStatus === '0') {
			setCurrentStep(1);
		} else if (windowStatus === '1' || windowStatus === '-1') {
			setCurrentStep(2);
		}

		if (stepsRef.current) {
			totalSteps.current = stepsRef.current.getAttribute('data-steps-total');
		}
	}, []);


	const handleNextStep = () => {
		setCurrentStep(currentStep + 1);
	};

	const handleBackStep = () => {
		setCurrentStep(currentStep - 1);
	};

	const {
		register,
		handleSubmit,
		formState,
		setValue,
		getValues
	} = useForm({
		mode: 'onChange'
	});

	const backToStart = () => {
		setCurrentStep(1);
		setFormSubmitError(false);
	};


	const onSubmit = async (formData) => {
		if (formSubmitInProgress) return;
		setFormSubmitError(false);
		setFormSubmitInProgress(true);

		if (formData.hasOwnProperty("file_upload") && formData.file_upload === undefined) delete formData["file_upload"];
		if (formData.hasOwnProperty("store_vat_nr") && formData.store_vat_nr === undefined) delete formData["store_vat_nr"];
		if (formData.hasOwnProperty("receipt_number") && formData.receipt_number === undefined) delete formData["receipt_number"];

		const {file_upload, ...restFormData} = formData;
		const formDataForSubmit = {...restFormData};

		const fetchURL = window.pathPrefix
			? `${process.env.REACT_APP_BASE_URL}/${window.pathPrefix}/api/joypepsico-app/v1/promotion/${window.promotionID}/participation`
			: `${process.env.REACT_APP_BASE_URL}/api/joypepsico-app/v1/promotion/${window.promotionID}/participation`;

		fetch(fetchURL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(formDataForSubmit),
		})
			.then(response => {
				if (!response.ok) {
					if (response.status >= 500) {
						throw new Error('Server error');
					} else {
						return response.json().then(errorData => {
							throw errorData;
						});
					}
				}

				return response.json();
			})
			.then(data => {
				setUserResponse(data);
				setFormSubmitError(false);
				handleNextStep();

				window.dataLayer.push({
					event: 'participation_submit',
					user_id: window.drupalSettings.pepsico_joy_eu_warehouse_reporting.user_id,
					event_id: 1.6,
					event_campaign: promotionSettings.promotion_page.promotion_title,
					message: 'ok',
					status: 'success'
				});
			})
			.catch(error => {
				setFormSubmitInProgress(false);
				setFormSubmitError(true);

				if (error.message === 'Server error') {
					setErrorMessageCode("continue_try_again");
				} else {
					setErrorMessageCode(error.user_continue);
					setErrorHeadline(error.headline);
					setErrorMessage(error.message);
				}

				window.dataLayer.push({
					event: 'participation_submit',
					user_id: window.drupalSettings.pepsico_joy_eu_warehouse_reporting.user_id,
					event_id: 1.6,
					event_campaign: promotionSettings.promotion_page.promotion_title,
					message: error.message === 'Server error' ? 'generic_error' : error.errors,
					status: 'fail'
				});
			})
			.finally(() => {
				setFormSubmitInProgress(false);
			});
	};
	const handlePostPromotionDataUpdate = () => {
        if (!userResponse) return; 
    
        const postPromotionData = getValues();
        const participationID = userResponse.result._id;
        const fetchURL = window.pathPrefix
            ? `${process.env.REACT_APP_BASE_URL}/${window.pathPrefix}/api/joypepsico-app/v1/promotion/${window.promotionID}/participation/${participationID}`
            : `${process.env.REACT_APP_BASE_URL}/api/joypepsico-app/v1/promotion/${window.promotionID}/participation/${participationID}`;
    
        setFormSubmitInProgress(true);
    
        fetch(fetchURL, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(postPromotionData),
        })
        .then(response => {
            if (!response.ok) {
                if (response.status >= 500) {
                    throw new Error('Server error');
                } else {
                    return response.json().then(errorData => {
                        throw errorData;
                    });
                }
            }
    
            return response.json();
        })
        .then(data => {
            setUserResponse(data);
            setFormSubmitError(false);
            handleNextStep();
    
            window.dataLayer.push({
                event: 'profile_enrichment_submit',
                user_id: window.drupalSettings.pepsico_joy_eu_warehouse_reporting.user_id,
                event_id: 1.15,
                event_campaign: promotionSettings.promotion_page.promotion_title,
                message: 'ok',
                status: 'success'
            });
        })
        .catch(error => {
            setFormSubmitInProgress(false);
            setFormSubmitError(true);
    
            if (error.message === 'Server error') {
                setErrorMessageCode("continue_try_again");
            } else {
                setErrorMessageCode(error.user_continue);
                setErrorHeadline(error.headline);
                setErrorMessage(error.message);
            }
    
            window.dataLayer.push({
                event: 'profile_enrichment_submit',
                user_id: window.drupalSettings.pepsico_joy_eu_warehouse_reporting.user_id,
                event_id: 1.15,
                event_campaign: promotionSettings.promotion_page.promotion_title,
                message: error.message === 'Server error' ? 'generic_error' : error.errors,
                status: 'fail'
            });
        })
        .finally(() => {
            setFormSubmitInProgress(false);
        });
    };

	return (
		<>
			<div
				className={`promo-container pepsi-ea-promo-type  step-${currentStep}`}>
				<Steps promotionSettings={promotionSettings} formSubmitError={formSubmitError} currentStep={currentStep}
					   stepsRef={stepsRef}/>
				{!promotionSettings.participationPeriodActive ? <>
					<PromotionPeriodNotification promotionSettings={promotionSettings}/>
				</> : <>
					{currentStep === 1 && (
						<div className="promo-step step-ea-connection">
							<div className="step-container">
								<PepsiEaConnection promotionSettings={promotionSettings}
												   onNextStep={handleNextStep}
												   formSubmitInProgress={formSubmitInProgress}/>
							</div>
						</div>
					)}
					{currentStep === 2 && (
						<div className="promo-step step-code-entry-form">
							<div className="form-container">
								<form className="webform-submission-promotion-form" onSubmit={handleSubmit(onSubmit)}>
									{
										formSubmitError && <ModalError
											backToStart={() => {
												setFormSubmitError(false);
											}}
											formSubmitInProgress={formSubmitInProgress}
											formSubmitError={formSubmitError}
											errorMessageCode={errorMessageCode}
											errorMessageHeadline={errorHeadline}
											errorMessageText={errorMessage}
										/>
									}
									<div className="promo-step-section">
										<CodeEntry
											handleBackStep={handleBackStep}
											handleNextStep={handleNextStep}
											register={register}
											formState={formState}
											promotionSettings={promotionSettings}/>
										<Questionaire
											handleBackStep={handleBackStep}
											handleNextStep={handleNextStep}
											register={register}
											formState={formState}
											promotionSettings={promotionSettings}
											formSubmitInProgress={formSubmitInProgress}
											formSubmitError={formSubmitError}
											backToStart={backToStart}/>
										<LegalAgreementCheckboxSet
											register={register}
											formState={formState}
											promotionSettings={promotionSettings}
											formSubmitInProgress={formSubmitInProgress}
											formSubmitError={formSubmitError}/>
										<div className="promo-fields-group">
											<div className="promo-fields promo-action-fields">
												<div className="promo-field info">
													<p>*{t("Required fields")}</p>
												</div>
												<div className="promo-field action">
													<div>
														{formSubmitInProgress ? <Loader/> :
															<>
																<a href={window.promotionHome}
																   className="btn btn-primary-text link">{t("Go Back")}
																</a>
																<input type="submit"
																	   className="btn btn-primary"
																	   disabled={!formState.isValid}
																	   value={t("Submit")}/>
															</>
														}
													</div>
												</div>
											</div>
										</div>
									</div>
								</form>
							</div>
						</div>
					)}
					{currentStep === 3 && (
						<div className="promo-step step-ea-connection">
							<div className="step-container">
								<div className="form-style-container form-container">
									<div className="promo-step-section">
										<Confirmation promotionSettings={promotionSettings}
													  userResponse={userResponse}/>
										<QuestionairePostPromotion
                                            register={register}
                                            formState={formState}
                                            userResponse={userResponse}
                                            formSubmitInProgress={formSubmitInProgress}
                                            onPostPromotionDataUpdate={handlePostPromotionDataUpdate}
                                        />
									</div>
								</div>
							</div>
						</div>
					)}
					{currentStep === 4 && (
						<div className="promo-step step-ea-connection">
							<div className="step-container">
								<div className="form-style-container form-container">
									<div className="promo-step-section">
										<ConfirmationPostPromotion/>
									</div>
								</div>
							</div>
						</div>
					)}
				</>
				}
			</div>
		</>
	);
}

export default PromotionCodeEntryEa;
